<template>
    <div id="aiCursorOverlay" aria-hidden="true">
        <div v-if="ghostVisible" class="ai-follow-ghost" :style="ghostStyle"></div>
        <div v-if="cursorVisible" class="ai-cursor" :class="{ pulsing: pulsing }" :style="{ transform: 'translate(' + x + 'px,' + y + 'px)' }">
            <svg viewBox="0 0 24 24" width="26" height="26">
                <path d="M4 2 L20 10 L13 12 L11 19 Z" fill="#009f89" stroke="white" stroke-width="1.2" stroke-linejoin="round" />
            </svg>
            <div v-if="label" class="ai-cursor-label">{{ label }}</div>
        </div>
        <div v-if="highlightVisible" class="ai-cursor-highlight" :style="highlightStyle"></div>
    </div>
</template>

<script>
    import { reducedMotion } from "./aiCursorMap"

    const MOVE_MS = 650
    const PULSE_MS = 400
    const HIGHLIGHT_MS = 2000
    const GHOST_MS = 450

    function wait(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms))
    }

    // Reine Darstellung fuer "Follow the AI": Cursor, Klick-Puls, Leuchten und der Chat, der in die Leiste fliegt. Was wann
    // passiert, entscheidet AiFollowBar.vue. Bei "reduzierter Bewegung" im Betriebssystem bleibt alles aus.
    export default {
        name: "AiCursor",
        data: () => ({
            cursorVisible: false,
            label: "",
            x: 0,
            y: 0,
            pulsing: false,
            highlightVisible: false,
            highlightStyle: {},
            ghostVisible: false,
            ghostStyle: {}
        }),
        methods: {
            async moveTo(el, label) {
                if (!el || reducedMotion()) return
                const rect = el.getBoundingClientRect()
                if (!rect.width || !rect.height) return
                this.label = label
                this.x = Math.round(rect.left + rect.width / 2)
                this.y = Math.round(rect.top + rect.height / 2)
                this.cursorVisible = true
                await wait(MOVE_MS)
            },
            async pulse() {
                if (!this.cursorVisible) return
                this.pulsing = true
                await wait(PULSE_MS)
                this.pulsing = false
            },
            showHighlight(el) {
                if (!el || reducedMotion()) return
                // Der Rahmen folgt der Kachel, solange er sichtbar ist: die Seite scrollt und laedt Zeilen nach, die Kachel verschiebt sich dabei.
                const place = () => {
                    const rect = el.getBoundingClientRect()
                    this.highlightStyle = { left: rect.left + "px", top: rect.top + "px", width: rect.width + "px", height: rect.height + "px" }
                }
                place()
                this.highlightVisible = true
                cancelAnimationFrame(this.highlightFrame)
                clearTimeout(this.highlightTimer)
                const end = Date.now() + HIGHLIGHT_MS
                const follow = () => {
                    if (!this.highlightVisible || Date.now() > end) return
                    place()
                    this.highlightFrame = requestAnimationFrame(follow)
                }
                this.highlightFrame = requestAnimationFrame(follow)
                this.highlightTimer = setTimeout(() => {
                    this.highlightVisible = false
                }, HIGHLIGHT_MS)
            },
            // Ein Rahmen fliegt von einem Bereich zum anderen: der Chat in die Leiste und wieder heraus.
            async flyGhost(fromEl, toEl) {
                if (!fromEl || !toEl || reducedMotion()) return
                const style = (rect) => ({ transform: "translate(" + rect.left + "px," + rect.top + "px)", width: rect.width + "px", height: rect.height + "px" })
                this.ghostStyle = style(fromEl.getBoundingClientRect())
                this.ghostVisible = true
                await this.$nextTick()
                await wait(20)
                this.ghostStyle = style(toEl.getBoundingClientRect())
                await wait(GHOST_MS)
                this.ghostVisible = false
            },
            hide() {
                this.cursorVisible = false
                this.highlightVisible = false
                this.ghostVisible = false
                this.label = ""
                clearTimeout(this.highlightTimer)
                cancelAnimationFrame(this.highlightFrame)
            }
        },
        mounted() {
            // Die App-Leiste hat ein transform: darin waere position:fixed relativ zur Leiste statt zum Fenster.
            document.body.appendChild(this.$el)
        },
        beforeDestroy() {
            clearTimeout(this.highlightTimer)
            cancelAnimationFrame(this.highlightFrame)
            if (this.$el.parentNode) this.$el.parentNode.removeChild(this.$el)
        }
    }
</script>

<style scoped>
    #aiCursorOverlay {
        position: fixed;
        inset: 0;
        pointer-events: none;
        z-index: 300;
    }
    .ai-cursor {
        position: fixed;
        top: 0;
        left: 0;
        transition: transform 0.65s cubic-bezier(0.4, 0, 0.2, 1);
        filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.4));
    }
    .ai-cursor.pulsing svg {
        animation: ai-cursor-pulse 0.4s ease;
    }
    .ai-cursor-label {
        margin-top: 4px;
        margin-left: 18px;
        background-color: #009f89;
        color: white;
        font-family: Nunito;
        font-size: 0.72em;
        padding: 2px 8px;
        border-radius: 10px;
        white-space: nowrap;
        width: max-content;
        max-width: 220px;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .ai-cursor-highlight {
        position: fixed;
        border: 2px solid #009f89;
        border-radius: 6px;
        box-shadow: 0 0 0 4px rgba(0, 159, 137, 0.2);
        transition:
            opacity 0.3s ease,
            top 0.4s ease,
            left 0.4s ease,
            width 0.4s ease,
            height 0.4s ease;
        animation: ai-cursor-glow 2s ease-out forwards;
    }
    @keyframes ai-cursor-pulse {
        0% {
            transform: scale(1);
        }
        40% {
            transform: scale(0.75);
        }
        100% {
            transform: scale(1);
        }
    }
    @keyframes ai-cursor-glow {
        0% {
            opacity: 1;
        }
        70% {
            opacity: 1;
        }
        100% {
            opacity: 0;
        }
    }
    .ai-follow-ghost {
        position: fixed;
        top: 0;
        left: 0;
        border: 2px solid #009f89;
        border-radius: 12px;
        background: rgba(0, 159, 137, 0.12);
        transition:
            transform 0.45s cubic-bezier(0.4, 0, 0.2, 1),
            width 0.45s cubic-bezier(0.4, 0, 0.2, 1),
            height 0.45s cubic-bezier(0.4, 0, 0.2, 1);
    }
</style>
